import type { ChangeEvent, Key } from "react";
import { memo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "./ValidatedQuestionsBySubjectDidOverview.styles";
import type { RevokeCredentialsMutation } from "@/src/graphql/generated/api/graphql";
import RevokeResultsDialog from "@/src/components/organisms/RevokeResultsDialog/RevokeResultsDialog";
import ConfirmRevoke from "@/src/components/organisms/ConfirmRevoke/ConfirmRevoke";
import SubjectDidFilter from "@/src/components/organisms/SelectSubjectDid/SelectSubjectDid";
import { format, setMinutes } from "date-fns";
import RevokedCredentialsDialog from "@/src/components/organisms/RevokedCredentialsDialog/RevokedCredentialsDialog";
import type {
    Credential,
    ExchangeProfile,
    IndicatorForOverview,
    SubjectDidsForOverview,
} from "./validatedQuestions.types";
import { useVirtualizer } from "@tanstack/react-virtual";
import { RevokedIndicatorButton } from "../../molecules/revoked-indicator-button/revoked-indicator-button";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type VirtualItem =
    | {
          type: "subjectDid";
          subjectDid: SubjectDidsForOverview;
          subjectDidIndex: number;
          id: string;
      }
    | {
          type: "exchangeProfile";
          subjectDid: SubjectDidsForOverview;
          profile: ExchangeProfile;
          subjectDidIndex: number;
          profileIndex: number;
          id: string;
      }
    | {
          type: "credential";
          subjectDid: SubjectDidsForOverview;
          profile: ExchangeProfile;
          credential: Credential;
          subjectDidIndex: number;
          profileIndex: number;
          credentialIndex: number;
          id: string;
      }
    | {
          type: "revokedIndicators";
          subjectDid: SubjectDidsForOverview;
          subjectDidIndex: number;
          id: string;
      }
    | {
          type: "divider";
          subjectDid: SubjectDidsForOverview;
          subjectDidIndex: number;
          id: string;
      };

export type ValidatedQuestionsOverviewProps = {
    loading?: boolean;
    revokeLoading?: boolean;
    subjectDids: SubjectDidsForOverview[];
    onRevoke: (indicators: IndicatorForOverview[], date: string) => void;
    onFilter: (filter: Key[]) => void;
    filter: Key[];
    revokeResults?: RevokeCredentialsMutation["revokeVerifiableQueryCredentials"];
    onCloseRevokeResults: () => void;
    openRevokeResults?: boolean;
};

type RevokedIndicatorDialog = {
    subjectDid: string;
    subjectDidLabel?: string;
};

function ValidatedQuestionsOverview({
    loading = false,
    revokeLoading = false,
    subjectDids = [],
    onRevoke = () => {},
    onFilter = () => {},
    filter = [],
    revokeResults = [],
    openRevokeResults = false,
    onCloseRevokeResults,
}: ValidatedQuestionsOverviewProps) {
    const { t } = useTranslation("common", {
        keyPrefix: "validated-questions",
    });
    const [selected, setSelected] = useState<string[]>([]);

    const [revokedIndicatorDialog, setRevokedIndicatorDialog] =
        useState<RevokedIndicatorDialog | null>(null);

    const parentRef = useRef<HTMLDivElement>(null);

    const revokedIndicatorsRefs = useRef<Map<string, HTMLDivElement>>(
        new Map()
    );

    // Flatten all items for virtualization
    const virtualItems: VirtualItem[] = subjectDids.flatMap(
        (subjectDid, subjectDidIndex) => {
            const items: VirtualItem[] = [];

            items.push({
                type: "subjectDid" as const,
                subjectDid,
                subjectDidIndex,
                id: `subjectDid-${subjectDid.id}`,
            });

            subjectDid.exchangeProfiles.forEach((profile, profileIndex) => {
                items.push({
                    type: "exchangeProfile" as const,
                    subjectDid,
                    profile,
                    subjectDidIndex,
                    profileIndex,
                    id: `profile-${subjectDid.id}-${profile.name}-${profile.version}`,
                });

                const sortedCredentials = [...profile.credentials].sort(
                    (a, b) =>
                        a.name.localeCompare(b.name, undefined, {
                            numeric: true,
                            sensitivity: "base",
                        })
                );

                sortedCredentials.forEach((credential, credentialIndex) => {
                    items.push({
                        type: "credential" as const,
                        subjectDid,
                        profile,
                        credential,
                        subjectDidIndex,
                        profileIndex,
                        credentialIndex,
                        id: `credential-${credential.id}`,
                    });
                });
            });

            items.push({
                type: "revokedIndicators" as const,
                subjectDid,
                subjectDidIndex,
                id: `revoked-${subjectDid.id}`,
            });

            if (subjectDidIndex < subjectDids.length - 1) {
                items.push({
                    type: "divider" as const,
                    subjectDid,
                    subjectDidIndex,
                    id: `divider-${subjectDid.id}`,
                });
            }

            return items;
        }
    );

    const virtualizer = useVirtualizer({
        count: virtualItems.length,
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => {
            const item = virtualItems[index];
            switch (item.type) {
                case "subjectDid":
                    return 62;
                case "exchangeProfile":
                    return 54;
                case "credential":
                    return 100;
                case "revokedIndicators":
                    return 60;
                case "divider":
                    return 20;
                default:
                    return 45;
            }
        },
        overscan: 5,
    });

    const registerRevokedIndicatorsRef = useCallback(
        (subjectDidId: number, element: HTMLDivElement | null) => {
            if (element) {
                revokedIndicatorsRefs.current.set(
                    subjectDidId.toString(),
                    element
                );
            } else {
                revokedIndicatorsRefs.current.delete(subjectDidId.toString());
            }
        },
        []
    );

    const [toRevoke, setToRevoke] = useState<{ id: string; name: string }[]>(
        []
    );

    const [revokeDate, setRevokeDate] = useState<string>(
        formatDate(new Date())
    );

    //
    // SubjectDid Checkboxes
    // -------------------------------------------------------------------------
    const isSubjectDidChecked = (subjectDid: SubjectDidsForOverview): boolean =>
        subjectDid.exchangeProfiles?.every((profile) =>
            profile.credentials.every((credential) =>
                selected.includes(credential.id)
            )
        );

    const isSubjectDidIndeterminate = (
        subjectDid: SubjectDidsForOverview
    ): boolean =>
        subjectDid.exchangeProfiles?.some((profile) =>
            profile.credentials.some((credential) =>
                selected.includes(credential.id)
            )
        );

    function handleSubjectDidOnChange(subjectDid: SubjectDidsForOverview) {
        return (event: ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked && subjectDid.exchangeProfiles) {
                setSelected(
                    [
                        ...selected,
                        ...subjectDid.exchangeProfiles.flatMap((ep) =>
                            ep.credentials.map((credential) => credential.id)
                        ),
                    ].flat()
                );
            } else {
                setSelected((prevSelection) =>
                    prevSelection.filter((id) =>
                        subjectDid.exchangeProfiles.every((profile) =>
                            profile.credentials.every(
                                (credential) => credential.id !== id
                            )
                        )
                    )
                );
            }
        };
    }

    function isExchangeProfileChecked(exchangeProfile: ExchangeProfile) {
        return exchangeProfile.credentials.every((credential) =>
            selected.includes(credential.id)
        );
    }

    function isExchangeProfileIndeterminate(exchangeProfile: ExchangeProfile) {
        return exchangeProfile.credentials.some((credential) =>
            selected.includes(credential.id)
        );
    }

    function handleExchangeProfileOnChange(exchangeProfile: ExchangeProfile) {
        return (event: ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelected((prevSelection) => [
                    ...prevSelection,
                    ...exchangeProfile.credentials.map((i) => i.id),
                ]);
            } else {
                setSelected((prevSelection) =>
                    prevSelection.filter(
                        (id) =>
                            !exchangeProfile.credentials.find(
                                (i) => i.id === id
                            )
                    )
                );
            }
        };
    }

    function handleSetRevokeDate(date: Date | null) {
        if (date === null) return;
        const formatted = formatDate(date);

        setRevokeDate(formatted);
    }

    //
    // Indicator Checkboxes
    // -------------------------------------------------------------------------

    const selectIndicator = (indicator: IndicatorForOverview) => {
        setSelected((prevState) => [...prevState, indicator.id]);
    };

    const deselectIndicator = (indicator: IndicatorForOverview) => {
        setSelected((prevState) =>
            prevState.filter((id) => id !== indicator.id)
        );
    };

    const toggleIndicator =
        (indicator: IndicatorForOverview) =>
        ({ target: { checked } }: ChangeEvent<HTMLInputElement>): any =>
            checked ? selectIndicator(indicator) : deselectIndicator(indicator);

    //
    // Bulk Menu Actions
    // -------------------------------------------------------------------------

    const revokeSingleItem = ({
        name,
        id,
    }: IndicatorForOverview | Credential) => {
        setToRevoke([{ name, id }]);
    };

    const revokeAllSelected = () => {
        const selectedItems = subjectDids
            .flatMap((subjectDid) =>
                subjectDid.exchangeProfiles.flatMap((profile) =>
                    profile.credentials.map((credential) => ({
                        id: credential.id,
                        name: credential.name,
                    }))
                )
            )
            .filter((item) => selected.includes(item.id));
        setToRevoke(selectedItems);
    };

    const cancelSelection = () => {
        setSelected([]);
    };

    //
    // SubjectDidFilter
    // -------------------------------------------------------------------------

    function handleFilterChange(event: { target: { value: Key[] } }) {
        onFilter(event.target.value);
    }

    const handleCloseRevokeResults = () => {
        onCloseRevokeResults();
        setToRevoke([]);
    };

    const handleShowRevokedCredentials = (
        subjectDidId: number,
        subjectDidLabel: string
    ) => {
        setRevokedIndicatorDialog({
            subjectDid: subjectDidId.toString(),
            subjectDidLabel: subjectDidLabel,
        });
    };

    const { hasPermission } = usePermissions();

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={t("title")}
                    {...(hasPermission(
                        "VERIFIABLE_QUERY_CREDENTIAL_CREATE"
                    ) && {
                        button: {
                            to: "new",
                            label: t("new-validated-question"),
                        },
                    })}
                />
                <styled.Main>
                    <SubjectDidFilter
                        value={filter}
                        onChange={handleFilterChange}
                    />

                    {loading && <styled.Loading />}

                    <div
                        style={{
                            overflowY: "auto",
                            contain: "strict",
                            height: "75vh",
                            paddingRight: "1rem",
                        }}
                        ref={parentRef}
                    >
                        <div
                            style={{
                                height: `${virtualizer.getTotalSize()}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {virtualizer
                                .getVirtualItems()
                                .map((virtualItem) => {
                                    const item =
                                        virtualItems[virtualItem.index];

                                    return (
                                        <div
                                            key={virtualItem.key}
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "100%",
                                                height: `${virtualItem.size}px`,
                                                transform: `translateY(${virtualItem.start}px)`,
                                            }}
                                        >
                                            {item.type === "subjectDid" && (
                                                <styled.ExchangeProfileControl
                                                    label={item.subjectDid.name}
                                                    variant="h2"
                                                    checkbox={{
                                                        checked:
                                                            isSubjectDidChecked(
                                                                item.subjectDid
                                                            ),
                                                        indeterminate:
                                                            isSubjectDidIndeterminate(
                                                                item.subjectDid
                                                            ),
                                                        onChange:
                                                            handleSubjectDidOnChange(
                                                                item.subjectDid
                                                            ),
                                                    }}
                                                />
                                            )}

                                            {item.type ===
                                                "exchangeProfile" && (
                                                <styled.ProfileRow>
                                                    <styled.ExchangeProfileControl
                                                        label={
                                                            item.profile.name
                                                        }
                                                        checkbox={{
                                                            checked:
                                                                isExchangeProfileChecked(
                                                                    item.profile
                                                                ),
                                                            indeterminate:
                                                                isExchangeProfileIndeterminate(
                                                                    item.profile
                                                                ),
                                                            onChange:
                                                                handleExchangeProfileOnChange(
                                                                    item.profile
                                                                ),
                                                        }}
                                                        pl={3}
                                                        pt={1}
                                                        variant="h4"
                                                    />
                                                    <styled.ProfileVersion
                                                        version={
                                                            item.profile.version
                                                        }
                                                    />
                                                </styled.ProfileRow>
                                            )}

                                            {item.type === "credential" && (
                                                <styled.IndicatorRowGroup>
                                                    <styled.IndicatorControl
                                                        key={`${item.profile.name}-${item.profile.version}-${item.credential.id}`}
                                                        checked={selected.includes(
                                                            item.credential.id
                                                        )}
                                                        even={
                                                            item.credentialIndex %
                                                                2 ===
                                                            0
                                                        }
                                                        onChange={toggleIndicator(
                                                            item.credential
                                                        )}
                                                        label={
                                                            item.credential.name
                                                        }
                                                        id={item.credential.id}
                                                        {...(hasPermission(
                                                            "VERIFIABLE_QUERY_CREDENTIAL_REVOKE"
                                                        ) && {
                                                            button: {
                                                                onClick:
                                                                    revokeSingleItem.bind(
                                                                        null,
                                                                        item.credential
                                                                    ),
                                                                label: "",
                                                            },
                                                        })}
                                                        status={
                                                            item.credential
                                                                .status
                                                        }
                                                        issuedAt={
                                                            item.credential
                                                                .issuedAt
                                                        }
                                                        hasPermissionToViewDetails={hasPermission(
                                                            "VERIFIABLE_QUERY_CREDENTIAL_READ"
                                                        )}
                                                    />
                                                </styled.IndicatorRowGroup>
                                            )}

                                            {item.type ===
                                                "revokedIndicators" && (
                                                <div
                                                    ref={(element) =>
                                                        registerRevokedIndicatorsRef(
                                                            item.subjectDid.id,
                                                            element
                                                        )
                                                    }
                                                >
                                                    <RevokedIndicatorButton
                                                        subjectDidId={
                                                            item.subjectDid.id
                                                        }
                                                        onClick={() =>
                                                            handleShowRevokedCredentials(
                                                                item.subjectDid
                                                                    .id,
                                                                item.subjectDid
                                                                    .name
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {item.type === "divider" && (
                                                <styled.EchangeProfileDivider
                                                    visible={true}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </styled.Main>
                <styled.Footer
                    visible={Boolean(selected.length)}
                    {...(hasPermission(
                        "VERIFIABLE_QUERY_CREDENTIAL_REVOKE"
                    ) && {
                        revokeButton: {
                            label: t("revoke-selected"),
                            onClick: revokeAllSelected,
                        },
                    })}
                    cancelSelectionButton={{
                        label: t("cancel-selection"),
                        onClick: cancelSelection,
                    }}
                />
            </styled.Container>

            <RevokedCredentialsDialog
                open={Boolean(revokedIndicatorDialog)}
                onClose={() => setRevokedIndicatorDialog(null)}
                subjectDid={revokedIndicatorDialog?.subjectDid || ""}
                subjectDidLabel={revokedIndicatorDialog?.subjectDidLabel || ""}
            />

            <RevokeResultsDialog
                open={openRevokeResults}
                results={revokeResults}
                onClose={handleCloseRevokeResults}
            />

            <ConfirmRevoke
                open={toRevoke.length > 0 && !loading && !openRevokeResults}
                loading={revokeLoading}
                indicators={toRevoke}
                handleDateChange={handleSetRevokeDate}
                onClose={({ confirmed }) => {
                    if (confirmed) {
                        onRevoke(toRevoke, revokeDate);
                    } else if (!confirmed && toRevoke.length > 0) {
                        setToRevoke([]);
                    }
                }}
            />
        </>
    );
}

export default memo(ValidatedQuestionsOverview);

export function formatDate(date: Date) {
    return format(setMinutes(date, 0), "yyyy-MM-dd'T'HH:mm");
}
